import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { BehaviorSubject, Observable, catchError, map, of, shareReplay, startWith, tap, switchMap } from 'rxjs';

import { WorldsService } from '../../api/worlds.service';

type WorldCreateVm = {
	loading: boolean;
	error: string | null;
	systems: Array<{ id: string; name: string }>;
	eras: Array<{ id: string; name: string }>;
	techLevels: Array<{ id: string; name: string }>;
};

@Component({
	selector: 'of-world-create-page',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule],
	template: `
		<section class="create-world-layout">
			<div class="content-area world-create-panel">
				<div class="section-heading">
					<div>
						<p class="kicker">Forge Core</p>
						<h3>Draft a new world</h3>
					</div>
					<button type="button" class="btn-outline" (click)="cancel()" [disabled]="submitting">Back to worlds</button>
				</div>

				<ng-container *ngIf="vm$ | async as vm">
					<div *ngIf="vm.error" class="form-alert error">
						{{ vm.error }}
						<button type="button" class="btn-outline slim" (click)="refresh()" style="margin-left: 0.75rem;">
							Retry
						</button>
					</div>

					<form class="forge-form" [formGroup]="form" (ngSubmit)="create()" novalidate>
						<label class="forge-field">
							<span>World name</span>
							<input
								type="text"
								name="name"
								placeholder="Kingdom of Eldoria"
								formControlName="name"
								autocomplete="off"
							/>
						</label>

						<label class="forge-field">
							<span>System / ruleset</span>
							<select
								name="systemRulesetId"
								formControlName="systemRulesetId"
								[disabled]="vm.loading || vm.systems.length === 0"
							>
								<option *ngFor="let option of vm.systems" [value]="option.id">{{ option.name }}</option>
							</select>
						</label>

						<label class="forge-field">
							<span>Era</span>
							<select name="eraId" formControlName="eraId" [disabled]="vm.loading || vm.eras.length === 0">
								<option *ngFor="let option of vm.eras" [value]="option.id">{{ option.name }}</option>
							</select>
						</label>

						<label class="forge-field">
							<span>Tech level</span>
							<select
								name="techLevelId"
								formControlName="techLevelId"
								[disabled]="vm.loading || vm.techLevels.length === 0"
							>
								<option *ngFor="let option of vm.techLevels" [value]="option.id">{{ option.name }}</option>
							</select>
						</label>

						<button
							type="submit"
							class="btn-primary-gradient"
							[disabled]="form.invalid || submitting || vm.loading"
						>
							{{ submitting ? 'Creating...' : 'Create world' }}
						</button>
					</form>

					<p *ngIf="vm.loading && !vm.error" class="support-text">Loading curated presets...</p>
					<p
						*ngIf="!vm.loading && !vm.error && (vm.systems.length === 0 || vm.eras.length === 0 || vm.techLevels.length === 0)"
						class="support-text"
					>
						Presets unavailable. Refresh to try again.
						<button type="button" class="btn-outline slim" (click)="refresh()" style="margin-left: 0.75rem;">
							Retry
						</button>
					</p>
				</ng-container>
			</div>
		</section>
	`,
})
export class WorldCreatePage {
	submitting = false;
	private readonly refresh$ = new BehaviorSubject<void>(undefined);

	form = this.fb.group({
		name: ['', [Validators.required, Validators.maxLength(200)]],
		systemRulesetId: ['', [Validators.required]],
		eraId: ['', [Validators.required]],
		techLevelId: ['', [Validators.required]],
	});

	vm$: Observable<WorldCreateVm>;

	constructor(
		private readonly worlds: WorldsService,
		private readonly fb: FormBuilder,
		private readonly router: Router
	) {
		this.vm$ = this.refresh$.pipe(
			switchMap(() =>
				this.worlds.list().pipe(
					map((res) => ({
						loading: false,
						error: null as string | null,
						systems: res.options.systems ?? [],
						eras: res.options.eras ?? [],
						techLevels: res.options.techLevels ?? [],
					})),
					catchError(() =>
						of({
							loading: false,
							error: 'Unable to load presets',
							systems: [] as Array<{ id: string; name: string }>,
							eras: [] as Array<{ id: string; name: string }>,
							techLevels: [] as Array<{ id: string; name: string }>,
						})
					),
					startWith({
						loading: true,
						error: null as string | null,
						systems: [] as Array<{ id: string; name: string }>,
						eras: [] as Array<{ id: string; name: string }>,
						techLevels: [] as Array<{ id: string; name: string }>,
					})
				)
			),
			tap((vm) => this.applyDefaults(vm)),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	create() {
		if (this.form.invalid || this.submitting) return;
		this.submitting = true;

		const value = this.form.getRawValue();
		this.worlds
			.create({
				name: value.name!.trim(),
				systemRulesetId: value.systemRulesetId!,
				eraId: value.eraId!,
				techLevelId: value.techLevelId!,
			})
			.subscribe({
				next: (res) => {
					this.submitting = false;
					void this.router.navigate(['/workspace/worlds', res.world.id]);
				},
				error: () => {
					this.submitting = false;
				},
			});
	}

	cancel() {
		void this.router.navigate(['/workspace']);
	}

	refresh() {
		this.refresh$.next();
	}

	private applyDefaults(vm: WorldCreateVm) {
		if (vm.loading || vm.error) return;

		const current = this.form.getRawValue();
		if (!current.systemRulesetId && vm.systems.length > 0) {
			this.form.patchValue({ systemRulesetId: vm.systems[0].id });
		}
		if (!current.eraId && vm.eras.length > 0) {
			this.form.patchValue({ eraId: vm.eras[0].id });
		}
		if (!current.techLevelId && vm.techLevels.length > 0) {
			this.form.patchValue({ techLevelId: vm.techLevels[0].id });
		}
	}
}
